import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Aviso, Botao, Selo } from '@via-tax/ui'
import { mensagemDeErro, useTRPC } from '../trpc'

const dataHora = (d: string | Date | null | undefined) => (d ? new Date(d).toLocaleString('pt-BR') : 'nunca')

export function Integracoes() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const status = useQuery({ ...trpc.admin.integracoes.queryOptions(), refetchInterval: 10_000 })
  const auditoria = useQuery(trpc.admin.verificarAuditoria.queryOptions())
  const sincronizar = useMutation(
    trpc.admin.sincronizarProcessos.mutationOptions({
      onSuccess: () => setTimeout(() => void qc.invalidateQueries({ queryKey: trpc.admin.integracoes.queryKey() }), 3000),
    }),
  )

  if (status.isPending) return <p className="text-sm text-tinta-3">Carregando…</p>
  if (status.isError) return <Aviso>{mensagemDeErro(status.error)}</Aviso>
  const p = status.data.processos
  const webhooks = p.webhooks24h as Record<string, number>

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Integrações</h1>
      </header>

      <section className="rounded-lg border border-linha bg-painel">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-linha px-5 py-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold">Via Processos — cadastro de empresas</h2>
            {!p.configurada ? (
              <Selo tom="alerta">Não configurada</Selo>
            ) : !p.tenantConfere ? (
              <Selo tom="alerta">Tenant diferente</Selo>
            ) : (
              <Selo tom="ok">Configurada</Selo>
            )}
          </div>
          <Botao onClick={() => sincronizar.mutate()} disabled={!p.configurada || sincronizar.isPending}>
            {sincronizar.isPending ? 'Enfileirando…' : 'Sincronizar agora'}
          </Botao>
        </div>
        <dl className="grid grid-cols-2 gap-px bg-linha sm:grid-cols-4">
          <Metrica rotulo="Clientes no espelho" valor={String(p.espelho.total)} />
          <Metrica rotulo="Habilitadas" valor={String(p.espelho.habilitadas)} />
          <Metrica rotulo="Webhooks 24 h" valor={String(Object.values(webhooks).reduce((a, b) => a + b, 0))} nota={webhooks.erro ? `${webhooks.erro} com erro` : undefined} />
          <Metrica rotulo="Último polling" valor={dataHora(p.ultimoPolling)} pequeno />
        </dl>
        <div className="flex flex-col gap-2 px-5 py-4 text-xs text-tinta-3">
          <p>
            Endereço: <span className="num text-tinta-2">{p.baseUrl ?? 'não definido'}</span>. Webhook em{' '}
            <span className="num text-tinta-2">POST /webhooks/processos</span>, com polling de segurança a cada 15 min e
            conciliação diária às 06:30.
          </p>
          {sincronizar.isSuccess && <p className="text-ok">Varredura completa enfileirada. Os números atualizam em instantes.</p>}
          {sincronizar.isError && <p className="text-alerta">{mensagemDeErro(sincronizar.error)}</p>}
          {!p.tenantConfere && p.configurada && (
            <p className="text-alerta">PROCESSOS_TENANT_ID aponta para outro escritório — o espelho não chega a este tenant.</p>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-linha bg-painel">
        <div className="border-b border-linha px-5 py-3">
          <h2 className="text-sm font-semibold">Alertas abertos</h2>
        </div>
        {status.data.alertas.length === 0 ? (
          <p className="px-5 py-4 text-sm text-tinta-3">Nenhum alerta. A última conciliação bateu com o Processos.</p>
        ) : (
          <ul className="divide-y divide-linha">
            {status.data.alertas.map((a) => (
              <li key={a.id} className="flex gap-3 px-5 py-3">
                <span className={`mt-1.5 size-2 shrink-0 rounded-full ${a.severidade === 'critico' ? 'bg-alerta' : a.severidade === 'aviso' ? 'bg-alerta/55' : 'bg-tinta-3'}`} aria-hidden />
                <div className="min-w-0">
                  <p className="text-[13px]">{a.mensagem}</p>
                  <p className="num mt-0.5 text-[11px] text-tinta-3">
                    {a.tipo} · {dataHora(a.criadoEm)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-linha bg-painel px-5 py-3">
        <div>
          <h2 className="text-sm font-semibold">Trilha de auditoria</h2>
          <p className="text-xs text-tinta-3">Cadeia de hash conferida contra a cabeça — detecta alteração no meio e truncamento no fim.</p>
        </div>
        {auditoria.isPending ? (
          <span className="text-xs text-tinta-3">Conferindo…</span>
        ) : auditoria.data?.ok ? (
          <Selo tom="ok">Íntegra · {auditoria.data.verificadas} registros</Selo>
        ) : (
          <Selo tom="alerta">Quebrada em {auditoria.data?.quebraEm ?? '?'}</Selo>
        )}
      </section>
    </div>
  )
}

function Metrica({ rotulo, valor, nota, pequeno }: { rotulo: string; valor: string; nota?: string; pequeno?: boolean }) {
  return (
    <div className="bg-painel px-5 py-4">
      <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-tinta-3">{rotulo}</dt>
      <dd className={`num mt-1 ${pequeno ? 'text-[13px]' : 'text-xl'}`}>{valor}</dd>
      {nota && <dd className="mt-0.5 text-[11px] text-alerta">{nota}</dd>}
    </div>
  )
}
